/**
 * Mini cross stitch patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const miniCrossStitchPatternsPack: ToilePack = {
    slug: 'mini-cross-stitch-patterns',
    category: 'craft',
    name: 'Mini cross stitch patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'plants', label: 'Plants', subject: 'a sheet of six mini cross-stitch charts of small potted plants, a cactus, a fern and a snake plant among them, each about twenty stitches tall'},
        {key: 'animals', label: 'Animals', subject: 'a sheet of six mini cross-stitch charts of small animals, a cat, a fox, a frog, a bee and a hedgehog among them, each under twenty stitches across'},
        {key: 'food', label: 'Food', subject: 'a sheet of six mini cross-stitch charts of small foods, a cupcake, a slice of pizza, a cup of tea and a strawberry among them, bright colours and a dark backstitch outline'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'clean colour charts, each stitch a small flat coloured X in its square, fine light-gray grid lines, a dark backstitch outline', palette: 'a limited palette of the floss colours named', framing: 'the set of charts centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'symbol charts for black-and-white printing, each stitch one small black symbol on white, one symbol per floss colour', palette: 'black ink only', framing: 'the set of charts filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'hand-coloured charts, stitches filled in with felt-tip pens, slightly uneven strokes, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the set of charts drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['a ghost', 'a rainbow', 'a snail'],
    directionHint: 'try: a ghost',
    seo: {
        title: 'Mini cross stitch patterns: plants, animals, food',
        metaDescription: 'Nine mini cross stitch sheets, each holding six tiny plant, animal or food motifs, in colour, symbol and hand-coloured styles.',
        h1: 'Mini cross stitch patterns: plants, animals and food',
        tagline: 'Small motifs, each about twenty stitches across, several to a sheet.',
        intro: 'Small cross-stitch designs are quick to finish and fit a bookmark, a card, a badge or a small hoop. Each of these nine sheets holds six tiny charts, about twenty stitches across. The sheets cover plants, animals and food. Each comes as colour charts with a dark backstitch outline, symbol charts for black-and-white printing, or hand-coloured sheets. Type a different motif and the same layout carries it.',
        useCases: [
            'a bookmark',
            'a card or a badge',
            'a small hoop',
            'a set of small gifts',
        ],
        faq: [
            {q: 'How big is each motif?', a: 'About twenty stitches across, so each one finishes small.'},
            {q: 'Are the outlines stitched too?', a: 'The colour charts show a dark backstitch outline around each motif.'},
            {q: 'Which style is for printing?', a: 'The symbol chart, which prints in black and white.'},
        ],
    },
}
